<template>
  <div
    :class="[$style.config, $style.wrap]"
    data-special-features-side-v="1.2.1"
  >
    <div :class="$style.content">
      <BaseHeading
        v-if="computedHeader"
        :as="computedHeaderWeight"
        size="h1"
        :class="$style.heading"
        :visually-hidden="computedVisuallyHideHeader"
      >
        {{ computedHeader }}
      </BaseHeading>
      <MarkedParser :class="$style.copy" :md="computedCopy" />
      <div v-if="computedCtas.length" :class="$style.ctas">
        <CallToAction
          v-for="cta in computedCtas?.filter(instanceOfCallToAction)"
          :key="cta.sys.id"
          :cta="cta"
          :class="$style.cta"
        />
      </div>
    </div>
    <div :class="$style.media">
      <picture v-if="computedImage" :class="$style.picture">
        <source :srcset="`${computedImage.url}?fm=avif`" type="image/avif" />
        <source :srcset="`${computedImage.url}?fm=webp`" type="image/webp" />
        <img
          :class="$style.image"
          :src="computedImage.url"
          :height="computedImage.height"
          :width="computedImage.width"
          loading="lazy"
          alt=""
        />
      </picture>
      <VideoContent
        v-if="computedVideo"
        :data="computedVideo"
        :class="$style.video"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TypeSpecialFeaturesSlideWithoutUnresolvableLinksResponse } from '~/types/contentful'

const props = defineProps<{
  data: TypeSpecialFeaturesSlideWithoutUnresolvableLinksResponse
}>()

const computedHeader = computed(() => props.data?.fields?.header)
const computedCtas = computed(() => props?.data?.fields?.ctas || [])
const computedHeaderWeight = computed(() => props?.data?.fields?.headerWeight)
const computedVisuallyHideHeader = computed(
  () => props?.data?.fields?.visuallyHideHeader,
)
const computedCopy = computed(() => props?.data?.fields?.copy)
const computedImage = computed(() => {
  if (instanceOfAsset(props.data?.fields?.image)) {
    const url = props.data?.fields?.image?.fields?.file?.url
    const width = props.data?.fields?.image?.fields?.file?.details?.image?.width
    const height =
      props.data?.fields?.image?.fields?.file?.details?.image?.height

    if (!url) return null
    return { height, url, width }
  }

  return null
})
const computedVideo = computed(() => props?.data?.fields?.video)
</script>

<style lang="scss" module>
.config {
  @include breakpoint-variables(
    '--special-features-slide-wrap-grid-columns',
    (
      base: 1fr,
      sm: 1fr 1fr,
      md: 1fr 1.5fr,
      lg: 1fr 1.5fr,
    )
  );

  @include breakpoint-variables(
    '--special-features-slide-wrap-grid-gap',
    (
      base: #{to-rem(30px)},
      sm: #{to-rem(35px)},
      md: #{to-rem(35px)},
      lg: #{to-rem(65px)},
    )
  );

  @include breakpoint-variables(
    '--special-features-slide-heading-margin',
    (
      base: 0 0 #{to-rem(18px)},
      sm: 0 0 #{to-rem(20px)},
      md: 0 0 #{to-rem(20px)},
      lg: 0 0 #{to-rem(20px)},
    )
  );

  @include breakpoint-variables(
    '--special-features-slide-copy-margin',
    (
      base: 0 0 #{to-rem(24px)},
      sm: 0 0 #{to-rem(24px)},
      md: 0 0 #{to-rem(30px)},
      lg: 0 0 #{to-rem(30px)},
    )
  );

  @include breakpoint-variables(
    '--special-features-slide-cta-width',
    (
      base: 100%,
      md: auto,
    )
  );
}

.wrap {
  display: grid;
  gap: var(--special-features-slide-wrap-grid-gap, to-rem(30px));
  grid-template-columns: var(--special-features-slide-wrap-grid-columns, 1fr);
}

.heading {
  margin: var(--special-features-slide-heading-margin, 0 0 to-rem(18px));
}

.copy {
  font-weight: var(--special-features-slide-copy-font-weight, 600);
  font-size: var(--special-features-slide-copy-font-size, to-rem(16px));
  line-height: var(--special-features-slide-copy-line-height, math.div(28, 16));
  margin: var(--special-features-slide-copy-margin, 0 0 to-rem(24px));
}

.ctas {
  display: flex;
  flex-wrap: wrap;
  gap: to-rem(18px);
}

.cta {
  width: var(--special-features-slide-cta-width, 100%);
}

.picture {
  display: block;
  width: 100%;
}

.image {
  height: auto;
  width: 100%;
}

.media {
  text-align: center;
  overflow: visible;

  .magicCard {
    display: inline-block;
  }
}
</style>
